import type { GridColumnVisibilityModel } from "@mui/x-data-grid";
import type { InHouseBarDataGroup } from "../../../components/UIToolkit/Graphs/BarChart/types";
import type { InHouseLineDataGroup } from "../../../components/UIToolkit/Graphs/LineChart/types";
import type { PerformanceHeatmapTone } from "../../../components/UIToolkit/Graphs/PerformanceHeatmap/types";
import { resolveReorderableSequence } from "../../../components/UIToolkit/Tables/SystemDataGrid/columnOrderUtils";
import { NEUTRAL } from "../../../theme/tokens";
import type { MultiFacilityPerformanceFacility, MultiFacilityPerformanceValue, MultiFacilitySeries, PushReportColumn } from "../../../utils/models";
import type { SortingState, SortOrder } from "../../../utils/multiColumnSorting";
import { isStringArray } from "../../../utils/typeGuards";
import { type ClampRange, costToKDollars, fetchedDataToChartDataGroup } from "../utils";

/**
 * Multi-facility chart-series palette — one stable, saturated hue per facility line so up to a
 * dozen facilities stay distinguishable in a legend. Functional/categorical (like CHART_SERIES):
 * intentionally NOT theme-driven. Assign deterministically by sorted facility order.
 */
export const CHART_MULTI_FACILITY_SERIES = [
	"#5B8DEF",
	"#E07A5F",
	"#3AA57A",
	"#B478DB",
	"#E8A33D",
	"#4FB8C9",
	"#E36FA0",
	"#8A9A5B",
	"#C0564B",
	"#5F5CE8",
	"#D4A017",
	"#48937E",
] as const;

/** Bold average anchor line on multi-facility charts — near-black so it reads above any series hue. */
export const CHART_MULTI_FACILITY_AVERAGE = NEUTRAL[800];

/** The five multi-facility disciplinary violation keys (BE `MULTI_DISCIPLINARY_VIOLATION_TYPES`). */
export type DisciplinaryViolationKey = "call_in" | "ncns" | "late_arrival" | "home_early" | "shift_delta";

/**
 * Disciplinary-violation stacked-bar segment palette — one stable hue per violation type, keyed by
 * the BE violation keys (`MULTI_DISCIPLINARY_VIOLATION_TYPES`). Functional/categorical (like
 * CHART_SERIES): intentionally NOT theme-driven, and echoing the single-mode disciplinary chart
 * hues so a violation type reads the same color across views (call_in swaps the theme-tracking
 * primary var for a stable blue).
 */
export const CHART_DISCIPLINARY_VIOLATIONS = {
	call_in: "#64B5F6",
	ncns: "#CE93D8",
	late_arrival: "#FFB5A7",
	home_early: "#FFC75F",
	shift_delta: "#E57373",
} as const satisfies Record<DisciplinaryViolationKey, string>;

/** Stroke width of the bold average anchor line (facility lines use the chart default 1.5). */
export const AVERAGE_LINE_STROKE_WIDTH = 2.5;

/**
 * Mirrors the backend's `MULTI_FACILITY_MAX_DEPARTMENTS` (`max_length` on the multi-facility QUERY
 * body) — requesting more returns 422. Real portfolios are far smaller; only internal super-users
 * (hundreds of accessible departments) hit this cap, and for them the view compares the first 50 in
 * sorted order. Raised 40 -> 50 alongside the selector's own cap so "select every facility" stays
 * reachable for the largest real networks; the two numbers must move together or "All" would ask
 * for more facilities than the endpoint accepts.
 */
export const MULTI_FACILITY_MAX_DEPARTMENTS = 50;

/**
 * Sorts and caps the accessible department ids at the backend limit while guaranteeing the
 * currently chosen department survives the cap (a >50-dept super-user whose facility sorts past
 * the limit must still see their own line — the whole default view). Swapping the chosen id in
 * for the last kept entry preserves ascending order, so the result is deterministic and stable
 * as a query-key part.
 */
export const capDepartmentIds = (departmentIds: string[], currentDepartmentId: string, max: number): string[] => {
	const sorted = [...departmentIds].sort();
	const capped = sorted.slice(0, max);
	if (sorted.length <= max || capped.includes(currentDepartmentId) || !sorted.includes(currentDepartmentId)) {
		return capped;
	}
	// currentDepartmentId sorts after every kept entry (it was cut by the cap), so appending it to
	// the first max-1 entries keeps the array sorted.
	return [...sorted.slice(0, max - 1), currentDepartmentId];
};

/**
 * The facilities the portfolio view actually compares: the user's accessible departments in the
 * chosen department's network (same `hospital`), sorted and capped like every multi query. This is
 * the SINGLE definition of "enough facilities for multi view" — the entry gate and the chart data
 * set must both read it, or a cross-network user can open a multi view whose queries are all
 * disabled and every chart hangs on the skeleton.
 */
export const multiFacilityDepartmentIds = (
	departments: ReadonlyArray<{ department_id: string; hospital: string }>,
	chosenHospital: string,
	chosenDepartmentId: string,
): string[] =>
	capDepartmentIds(
		departments.filter((department) => department.hospital === chosenHospital).map((department) => department.department_id),
		chosenDepartmentId,
		MULTI_FACILITY_MAX_DEPARTMENTS,
	);

/**
 * Deterministic facility→color assignment: palette order over the sorted department ids, wrapping
 * when there are more facilities than palette entries. Same ids ⇒ same colors on every chart.
 */
export const assignFacilityColors = (sortedDepartmentIds: string[]): Record<string, string> =>
	Object.fromEntries(sortedDepartmentIds.map((id, index) => [id, CHART_MULTI_FACILITY_SERIES[index % CHART_MULTI_FACILITY_SERIES.length]]));

/**
 * Facility display names keyed by department id, disambiguated: when two facilities share a
 * display name, the second/third/... (in sorted-id order) get a " (2)" / " (3)" suffix. The chart
 * uses series *names* as visibility keys, so duplicates would otherwise collapse into one series.
 */
export const dedupeFacilityDisplayNames = (sortedDepartmentIds: string[], facilityNames: Record<string, string>): Record<string, string> => {
	const seenCounts: Record<string, number> = {};
	const result: Record<string, string> = {};
	for (const id of sortedDepartmentIds) {
		// DEFAULT: the BE contract returns a name per requested facility; the id fallback only
		// guards a malformed response so the legend never renders an empty label.
		const name = facilityNames[id] || id;
		const timesSeen = (seenCounts[name] || 0) + 1;
		seenCounts[name] = timesSeen;
		result[id] = timesSeen === 1 ? name : `${name} (${timesSeen})`;
	}
	return result;
};

/**
 * Order-preserving intersection for the page-level facility selector: the result follows
 * `sortedDepartmentIds` order regardless of the order facilities were clicked in, and unknown or
 * stale selected ids (e.g. a facility that fell out of the capped set) are dropped. This is the
 * id list the charts BUILD series from — selection must never reach a query key.
 */
export const filterVisibleDepartmentIds = (sortedDepartmentIds: string[], selectedIds: string[]): string[] => {
	const selected = new Set(selectedIds);
	return sortedDepartmentIds.filter((id) => selected.has(id));
};

/**
 * IANA timezone -> region label. A facility carries no region field, so the timezone is the only
 * geography the auth payload already knows; these are the zones production departments actually use
 * (checked against the department table). Phoenix groups under Mountain deliberately: the grouping
 * is geographic, and Arizona skipping DST doesn't move it to another region.
 */
const REGION_BY_TIMEZONE: Record<string, string> = {
	"America/New_York": "mc.multi.region.eastern",
	"America/Detroit": "mc.multi.region.eastern",
	"America/Toronto": "mc.multi.region.eastern",
	"America/Chicago": "mc.multi.region.central",
	"America/Winnipeg": "mc.multi.region.central",
	"America/Denver": "mc.multi.region.mountain",
	"America/Phoenix": "mc.multi.region.mountain",
	"America/Edmonton": "mc.multi.region.mountain",
	"America/Los_Angeles": "mc.multi.region.pacific",
	"America/Vancouver": "mc.multi.region.pacific",
	"America/Anchorage": "mc.multi.region.alaska",
	"Pacific/Honolulu": "mc.multi.region.hawaii",
	"Asia/Jerusalem": "mc.multi.region.israel",
};

/**
 * The region a facility is grouped under in the selector, as either a translation key (the known
 * zones above) or a literal label.
 *
 * A zone outside the table falls back to its IANA city ("Africa/Cairo" -> "Cairo") rather than an
 * "Other" bucket, so an unmapped facility still groups somewhere meaningful and reads correctly
 * without a code change. DEFAULT: a department with no timezone at all cannot be placed
 * geographically, so it groups under an explicit "Unspecified" heading — surfaced rather than
 * silently folded into a real region, since it usually means the department row is misconfigured.
 */
export const facilityRegion = (timezone: string | undefined): { key: string; isTranslationKey: boolean } => {
	if (!timezone) return { key: "mc.multi.region.unspecified", isTranslationKey: true };
	const mapped = REGION_BY_TIMEZONE[timezone];
	if (mapped) return { key: mapped, isTranslationKey: true };
	if (timezone === "UTC" || timezone === "Etc/UTC") return { key: "UTC", isTranslationKey: false };
	const city = timezone.split("/").pop() ?? timezone;
	return { key: city.replace(/_/g, " "), isTranslationKey: false };
};

/** Display order for the known regions: west to east across North America, then everything else. */
const REGION_ORDER = [
	"mc.multi.region.pacific",
	"mc.multi.region.mountain",
	"mc.multi.region.central",
	"mc.multi.region.eastern",
	"mc.multi.region.alaska",
	"mc.multi.region.hawaii",
	"mc.multi.region.israel",
];

export type FacilitySelectorGroup = {
	/** Region heading text, already translated by the caller. */
	label: string;
	/** Facility ids under this heading, in sorted-id order. */
	departmentIds: string[];
	/** Facilities under this heading, in sorted-id order. */
	items: { value: string; label: string }[];
};

/**
 * Facilities for the page-level multi-select, bucketed by region and ordered west-to-east (unknown
 * regions alphabetically after the known ones). Labels run through `dedupeFacilityDisplayNames` over
 * the FULL capped set so a duplicate-named facility carries the same " (2)" suffix in the selector as
 * in every chart legend.
 *
 * `translateRegion` resolves a region translation key; literal fallback labels (an unmapped IANA
 * city) pass through untouched, which keeps this helper i18n-free and unit-testable.
 */
export const buildFacilitySelectorGroups = (
	sortedDepartmentIds: string[],
	authDepartments: Array<{ department_id: string; display_name: string; timezone?: string }>,
	translateRegion: (key: string) => string,
): FacilitySelectorGroup[] => {
	const namesById = Object.fromEntries(authDepartments.map((department) => [department.department_id, department.display_name]));
	const zonesById = Object.fromEntries(authDepartments.map((department) => [department.department_id, department.timezone]));
	const displayNames = dedupeFacilityDisplayNames(sortedDepartmentIds, namesById);

	const buckets = new Map<string, { label: string; order: number; departmentIds: string[] }>();
	for (const id of sortedDepartmentIds) {
		const region = facilityRegion(zonesById[id]);
		const label = region.isTranslationKey ? translateRegion(region.key) : region.key;
		const knownIndex = REGION_ORDER.indexOf(region.key);
		// Known regions keep their west-to-east slot; everything else sorts after them by label.
		const order = knownIndex >= 0 ? knownIndex : REGION_ORDER.length;
		const bucket = buckets.get(region.key) ?? { label, order, departmentIds: [] };
		bucket.departmentIds.push(id);
		buckets.set(region.key, bucket);
	}

	return [...buckets.values()]
		.sort((a, b) => a.order - b.order || a.label.localeCompare(b.label))
		.map((bucket) => ({
			label: bucket.label,
			departmentIds: bucket.departmentIds,
			items: bucket.departmentIds.map((id) => ({ value: id, label: displayNames[id] })),
		}));
};

/**
 * Flat items for the page-level facility multi-select: one `{value, label}` per capped department, in
 * sorted-id order. Retained for callers that don't group (and as the flattening of the grouped shape).
 */
export const buildFacilitySelectorItems = (
	sortedDepartmentIds: string[],
	authDepartments: Array<{ department_id: string; display_name: string }>,
): { value: string; label: string }[] => {
	const namesById = Object.fromEntries(authDepartments.map((department) => [department.department_id, department.display_name]));
	const displayNames = dedupeFacilityDisplayNames(sortedDepartmentIds, namesById);
	return sortedDepartmentIds.map((id) => ({ value: id, label: displayNames[id] }));
};

/** Decimal places kept on the computed average values (matches the charts' tooltip/tick precision). */
const AVERAGE_VALUE_PRECISION = 2;

/**
 * Per-date simple mean over the given facilities' series: for each date reported by at least one
 * of them, the unweighted average of the facilities that have a value on that date. This is what
 * the bold "Average" anchor line shows — the average of the lines actually drawn on the chart
 * (locked product decision: NOT the BE census-weighted portfolio average, which covers every
 * facility and can't be re-scoped client-side without census weights). Date keys are averaged as
 * raw strings; all facilities of a branch share the same day/week bucketing, so keys align.
 */
export const meanSeriesPerDate = (perFacility: Record<string, Record<string, number>>, departmentIds: string[]): Record<string, number> => {
	const accumulator: Record<string, { total: number; count: number }> = {};
	for (const id of departmentIds) {
		const facilitySeries = perFacility[id];
		if (!facilitySeries) continue;
		for (const [date, value] of Object.entries(facilitySeries)) {
			accumulator[date] ??= { total: 0, count: 0 };
			accumulator[date].total += value;
			accumulator[date].count += 1;
		}
	}
	return Object.fromEntries(
		Object.entries(accumulator).map(([date, { total, count }]) => [date, Number.parseFloat((total / count).toFixed(AVERAGE_VALUE_PRECISION))]),
	);
};

/**
 * Converts one multi-facility series into line-chart groups: one group per facility (sorted-id
 * order, deterministic colors) plus the bold average anchor group appended last so it draws on
 * top. The anchor is the per-date mean of `averageDepartmentIds` — the caller decides whether that
 * is all facilities in the set (a consistent baseline) or just the drawn ones. It's shown whenever
 * at least one facility line is drawn (so a single selected facility can still be compared against
 * the network average), and omitted only when nothing is on the chart.
 */
export const multiStatsToLineGroups = ({
	series,
	sortedDepartmentIds,
	averageDepartmentIds,
	displayNames,
	colors,
	averageName,
	timezone,
	clampRange,
}: {
	series: MultiFacilitySeries;
	sortedDepartmentIds: string[];
	/** The facilities the average anchor is computed over (drawn = selected minus legend-hidden). */
	averageDepartmentIds: string[];
	displayNames: Record<string, string>;
	colors: Record<string, string>;
	/** Already-translated legend label for the average line. */
	averageName: string;
	timezone: string;
	clampRange?: ClampRange;
}): InHouseLineDataGroup[] => {
	const facilityGroups = sortedDepartmentIds
		.filter((id) => series.perFacility[id])
		.map((id) =>
			fetchedDataToChartDataGroup({
				data: series.perFacility[id],
				name: displayNames[id],
				color: colors[id],
				timezone,
				clampRange,
			}),
		);
	if (facilityGroups.length < 1) return facilityGroups;
	const averageGroup: InHouseLineDataGroup = {
		...fetchedDataToChartDataGroup({
			data: meanSeriesPerDate(series.perFacility, averageDepartmentIds),
			name: averageName,
			color: CHART_MULTI_FACILITY_AVERAGE,
			timezone,
			clampRange,
		}),
		strokeWidth: AVERAGE_LINE_STROKE_WIDTH,
	};
	return [...facilityGroups, averageGroup];
};

/** Headroom added above/below the visible series' extremes so lines don't touch the plot edges. */
const Y_DOMAIN_PADDING_RATIO = 0.05;
/** Decimal places kept on the computed Y-domain bounds (matches the charts' 1-2 decimal ticks). */
const Y_DOMAIN_PRECISION = 2;

/**
 * Shared Y-axis domain over the *visible* series only (padded by Y_DOMAIN_PADDING_RATIO, floored
 * at 0), so toggling a facility in/out of the legend rescales the axis to what's actually drawn.
 */
export const visibleYDomain = (groups: InHouseLineDataGroup[], hiddenSeries: ReadonlySet<string>): { min: number; max: number } | undefined => {
	const values = groups.filter((g) => !hiddenSeries.has(g.name)).flatMap((g) => g.data.map((item) => item.value));
	if (values.length === 0) return undefined;
	const max = Math.max(...values);
	const min = Math.min(...values);
	// Flat or all-zero series still get non-zero headroom: fall back to the magnitude, then to 1.
	const padding = (max - min || Math.abs(max) || 1) * Y_DOMAIN_PADDING_RATIO;
	return {
		min: Number.parseFloat(Math.max(0, min - padding).toFixed(Y_DOMAIN_PRECISION)),
		max: Number.parseFloat((max + padding).toFixed(Y_DOMAIN_PRECISION)),
	};
};

/**
 * Selection-aware emptiness check: true when at least one page-SELECTED facility carries a
 * datapoint. The average anchor needs no separate check — it is derived from the drawn
 * facilities, so it cannot have data when they are all empty.
 */
export const hasAnySeriesData = (series: MultiFacilitySeries, visibleDepartmentIds: string[]): boolean =>
	visibleDepartmentIds.some((id) => {
		const facilitySeries = series.perFacility[id];
		return facilitySeries !== undefined && Object.keys(facilitySeries).length > 0;
	});

/**
 * Canonical order of the five multi-facility disciplinary violation types — matches the backend's
 * `MULTI_DISCIPLINARY_VIOLATION_TYPES` (which zero-fills every type per facility, so the response
 * shape is stable). Drives both the stack segment order and the legend order. Typed against the
 * token key union so a typo'd key fails to compile instead of silently missing its color.
 */
export const DISCIPLINARY_VIOLATION_ORDER = [
	"call_in",
	"ncns",
	"late_arrival",
	"home_early",
	"shift_delta",
] as const satisfies readonly DisciplinaryViolationKey[];

/**
 * Converts the disciplinary summary into categorical stacked-bar groups: one stacked column per
 * facility (X label = deduped display name) with the five violation types as segments in
 * DISCIPLINARY_VIOLATION_ORDER, colored from CHART_DISCIPLINARY_VIOLATIONS. Facilities are ordered
 * worst-first (descending total violations); ties keep sorted-id order (stable sort over the
 * already-sorted ids).
 */
export const disciplinaryToStackedBarGroups = ({
	perFacility,
	sortedDepartmentIds,
	displayNames,
	violationLabels,
}: {
	perFacility: Record</*departmentId*/ string, Record</*violationType*/ string, number>>;
	sortedDepartmentIds: string[];
	displayNames: Record<string, string>;
	/** Violation key → already-translated segment name (keeps this helper i18n-free and pure). */
	violationLabels: Record<string, string>;
}): InHouseBarDataGroup[][] => {
	const presentIds = sortedDepartmentIds.filter((id) => perFacility[id]);
	// The BE zero-fills all five violation types per facility (stable shape), so direct access is safe.
	const totals = Object.fromEntries(presentIds.map((id) => [id, DISCIPLINARY_VIOLATION_ORDER.reduce((sum, violation) => sum + perFacility[id][violation], 0)]));
	const worstFirstIds = [...presentIds].sort((a, b) => totals[b] - totals[a]);
	return [
		DISCIPLINARY_VIOLATION_ORDER.map((violation) => ({
			name: violationLabels[violation],
			color: CHART_DISCIPLINARY_VIOLATIONS[violation],
			data: worstFirstIds.map((id) => ({ dateOrLabel: displayNames[id], value: perFacility[id][violation] })),
		})),
	];
};

/** True when any facility reports at least one violation (the BE zero-fills, so all-zero means "no data"). */
export const hasAnyDisciplinaryData = (perFacility: Record<string, Record<string, number>>): boolean =>
	Object.values(perFacility).some((counts) => Object.values(counts).some((count) => count > 0));

/** Default Y-axis tick for the unit-less multi-facility charts (HPPD, pickups): trimmed one-decimal number. */
export const plainAxisTick = (value: number): string => `${Number.parseFloat(value.toFixed(1))}`;

/**
 * Y-axis tick formatter for the dollar-scale multi-facility charts (PPD + the three cost charts).
 * Thousands compact to "$X.XK" — a raw "21832.5" tick is wider than the axis strip and clips —
 * and smaller values keep one decimal, mirroring the single-mode dollar-prefixed axes.
 */
export const costAxisTick = (value: number): string =>
	value >= 1000 ? `$${Number.parseFloat((value / 1000).toFixed(1))}K` : `$${Number.parseFloat(value.toFixed(1))}`;

/** Presentation formats for the multi-facility KPI header row. */
export type MultiFacilityKpiFormat = "dollars2" | "decimal2" | "percent1" | "kDollars" | "integer";

/**
 * Formats one KPI value for the header row. `null`/`undefined` (BE-blanked KPI, zero denominator,
 * or still loading) render as a dash — never a fake 0.
 */
export const formatMultiFacilityKpi = (value: number | null | undefined, format: MultiFacilityKpiFormat): string => {
	if (value === null || value === undefined) return "-";
	switch (format) {
		case "dollars2":
			return `$${value.toFixed(2)}`;
		case "decimal2":
			return value.toFixed(2);
		case "percent1":
			return `${value.toFixed(1)}%`;
		case "kDollars":
			// costToKDollars only returns undefined for an undefined input, which the guard above excludes.
			return costToKDollars(value) ?? "-";
		case "integer":
			return value.toFixed(0);
	}
};

export type PerformanceMetric = "hppd" | "ppd";
export type PerformanceDisplayMode = "value" | "difference";
export type PerformanceSort = "current" | "unfavorable" | "favorable";
type TargetDistanceBand = { maximum: number; tone: Exclude<PerformanceHeatmapTone, "neutral"> };

/** At this absolute target-distance threshold, use the heatmap's strongest signal. */
export const PERFORMANCE_DARK_RED_DISTANCE = 0.5;

/** Treat scores within floating-point noise as equal so alphabetical ordering remains stable. */
const PERFORMANCE_SORT_TIE_EPSILON = 1e-9;

/** Target-distance bands used by the generic heatmap severity ramp. */
export const PERFORMANCE_TARGET_DISTANCE_BANDS: readonly TargetDistanceBand[] = [
	{ maximum: 0.05, tone: "success" },
	{ maximum: 0.08, tone: "watch" },
	{ maximum: 0.12, tone: "caution" },
	{ maximum: 0.17, tone: "elevated" },
	{ maximum: 0.23, tone: "high" },
	{ maximum: 0.3, tone: "alert" },
	{ maximum: 0.4, tone: "danger" },
	{ maximum: PERFORMANCE_DARK_RED_DISTANCE, tone: "critical" },
];

/** Returns the server-aggregated actual/target pairs for one facility and selected metric. */
export const performanceValuesFor = (facility: MultiFacilityPerformanceFacility, metric: PerformanceMetric): MultiFacilityPerformanceValue[] =>
	facility[metric];

/**
 * Absolute percentage distance from target. Missing, zero, or invalid denominators are not
 * comparable and return null rather than a fabricated percentage.
 */
export const performanceDistanceFromTarget = ({ actual, target }: MultiFacilityPerformanceValue): number | null => {
	if (actual === null || target === null || target <= 0) return null;
	return Math.abs(actual - target) / target;
};

/** Maps an absolute target distance onto the generic heatmap's escalating severity ramp. */
export const performanceCellTone = (value: MultiFacilityPerformanceValue): PerformanceHeatmapTone => {
	const distance = performanceDistanceFromTarget(value);
	if (distance === null) return "neutral";
	if (distance >= PERFORMANCE_DARK_RED_DISTANCE) return "severe";
	return PERFORMANCE_TARGET_DISTANCE_BANDS.find((band) => distance <= band.maximum)?.tone ?? "critical";
};

/**
 * Per-week plain mean of the displayed facilities, as one synthetic row for the bottom of the matrix.
 *
 * Unweighted on purpose, matching the comparison charts' "Average" anchor (`meanSeriesPerDate`) — a
 * locked product decision to average the SERIES ON SCREEN rather than reproduce the backend's
 * census-weighted portfolio figure, which cannot be re-scoped client-side without census weights.
 * `actual` and `target` are averaged separately, so the Difference view shows mean-actual minus
 * mean-target and every cell stays consistent with the row above it.
 *
 * `excludeDepartmentIds` drops facilities whose values are placeholders rather than measurements —
 * a wage-restricted facility's PPD is zeroed, and averaging those zeroes in would drag the network
 * figure toward nothing for data the reader was never shown.
 */
export const performanceAverageRow = (
	facilities: MultiFacilityPerformanceFacility[],
	metric: PerformanceMetric,
	weekCount: number,
	excludeDepartmentIds: ReadonlySet<string> = new Set(),
): MultiFacilityPerformanceValue[] => {
	const contributing = facilities.filter((facility) => !excludeDepartmentIds.has(facility.departmentId));
	return Array.from({ length: weekCount }, (_unused, weekIndex) => {
		const cells = contributing.map((facility) => performanceValuesFor(facility, metric)[weekIndex]).filter((cell) => cell !== undefined);
		const actuals = cells.map((cell) => cell.actual).filter((value): value is number => value !== null);
		const targets = cells.map((cell) => cell.target).filter((value): value is number => value !== null);
		const censuses = cells.map((cell) => cell.census).filter((value): value is number => value !== null);
		return {
			actual: mean(actuals),
			target: mean(targets),
			census: mean(censuses),
		};
	});
};

/** Mean of the reported values, or null when nothing reported — never a stand-in zero. */
const mean = (values: number[]): number | null => (values.length === 0 ? null : values.reduce((total, value) => total + value, 0) / values.length);

/** Signed actual-minus-target delta used in both the Difference view and the cell tooltip. */
export const performanceVariance = ({ actual, target }: MultiFacilityPerformanceValue): number | null => {
	if (actual === null || target === null) return null;
	return actual - target;
};

/**
 * Normalizes each cell to a metric-aware unfavorable score for sort only. Below-target HPPD and
 * above-target PPD are positive; the opposite directions are negative. It never recalculates
 * HPPD/PPD, which remain backend-owned census-weighted values.
 */
export const performanceDirectionalScore = (value: MultiFacilityPerformanceValue, metric: PerformanceMetric): number | null => {
	const distance = performanceDistanceFromTarget(value);
	if (distance === null || value.actual === null || value.target === null) return null;
	const variance = (value.actual - value.target) / value.target;
	return metric === "hppd" ? -variance : variance;
};

/** Mean directional score over comparable weeks; no comparable week stays null and sorts last. */
export const facilityPerformanceScore = (facility: MultiFacilityPerformanceFacility, metric: PerformanceMetric): number | null => {
	const scores = performanceValuesFor(facility, metric)
		.map((value) => performanceDirectionalScore(value, metric))
		.filter((score): score is number => score !== null);
	if (scores.length === 0) return null;
	return scores.reduce((total, score) => total + score, 0) / scores.length;
};

const comparePerformanceNames = (left: MultiFacilityPerformanceFacility, right: MultiFacilityPerformanceFacility): number =>
	left.departmentName.localeCompare(right.departmentName);

/**
 * Orders only the selected facility rows. The default keeps the current facility first and then
 * alphabetical; performance sorts place missing comparable data after measured facilities.
 */
export const sortPerformanceFacilities = ({
	facilities,
	metric,
	sort,
	currentDepartmentId,
}: {
	facilities: MultiFacilityPerformanceFacility[];
	metric: PerformanceMetric;
	sort: PerformanceSort;
	currentDepartmentId: string;
}): MultiFacilityPerformanceFacility[] => {
	const sorted = [...facilities];
	if (sort === "current") {
		return sorted.sort((left, right) => {
			if (left.departmentId === currentDepartmentId) return -1;
			if (right.departmentId === currentDepartmentId) return 1;
			return comparePerformanceNames(left, right);
		});
	}
	return sorted.sort((left, right) => {
		const leftScore = facilityPerformanceScore(left, metric);
		const rightScore = facilityPerformanceScore(right, metric);
		if (leftScore === null && rightScore === null) return comparePerformanceNames(left, right);
		if (leftScore === null) return 1;
		if (rightScore === null) return -1;
		const comparison = leftScore - rightScore;
		const isTie = Math.abs(comparison) <= PERFORMANCE_SORT_TIE_EPSILON;
		return isTie ? comparePerformanceNames(left, right) : sort === "unfavorable" ? -comparison : comparison;
	});
};

/**
 * The emailed report's columns: the visible ones, in the order the table shows them. The saved order is
 * reconciled with the SAME rule the grid uses (`resolveReorderableSequence`), so a column the saved order
 * doesn't mention — one added after the user last dragged — sits after its natural predecessor in the
 * sheet exactly as it does on screen, instead of trailing the sheet. An empty saved order resolves to
 * the catalog order.
 */
export const orderEmailedColumns = (
	exportableColumns: PushReportColumn[],
	visibilityModel: GridColumnVisibilityModel,
	savedFieldOrder: string[],
): PushReportColumn[] => {
	const byKey = new Map(exportableColumns.map((column) => [column.key, column]));
	const sequence = resolveReorderableSequence(
		savedFieldOrder,
		exportableColumns.map((column) => column.key),
	);
	return sequence.flatMap((key) => {
		const column = byKey.get(key);
		return column && visibilityModel[key] !== false ? [column] : [];
	});
};

/**
 * Grid fields that changed identity since the previous storage version: renamed ones map to the field now
 * holding their column, retired ones no longer exist. Applied once, when migrating a saved layout.
 */
const LEGACY_GRID_FIELD_RENAMES: Readonly<Record<string, string>> = { staffPickupPercent: "shiftPickupPercent" };
const RETIRED_GRID_FIELDS: ReadonlySet<string> = new Set(["bedsideLoginPercent", "adminActiveDays", "adminActionsCount"]);

const renameLegacyGridField = (field: string): string => (Object.hasOwn(LEGACY_GRID_FIELD_RENAMES, field) ? LEGACY_GRID_FIELD_RENAMES[field] : field);

/** A saved field order with retired fields dropped and renamed ones pointed at their current field. */
export const renameLegacyGridFields = (savedOrder: readonly string[]): string[] =>
	savedOrder.filter((field) => !RETIRED_GRID_FIELDS.has(field)).map(renameLegacyGridField);

/**
 * Regroups a saved field order so each header band's fields are contiguous — the user's relative order
 * is kept inside each band, bands sit where their first field did, and ungrouped fields (the pinned
 * identity columns) keep their relative positions. A drag saved under an older column set can scatter a
 * band (e.g. a column that used to live elsewhere), which would render it as two header cells; this
 * restores one cell per band without discarding the user's arrangement. Fields the saved order doesn't
 * mention are not added — the grid slots those in itself.
 */
export const normalizeBandContiguousOrder = (savedOrder: readonly string[], bandForField: (field: string) => string | undefined): string[] => {
	const bandAnchor = new Map<string, number>();
	const anchors = savedOrder.map((field, index) => {
		const band = bandForField(field);
		if (band === undefined) return index;
		const anchor = bandAnchor.get(band);
		if (anchor !== undefined) return anchor;
		bandAnchor.set(band, index);
		return index;
	});
	return savedOrder
		.map((field, index) => ({ field, index, anchor: anchors[index] }))
		.sort((a, b) => a.anchor - b.anchor || a.index - b.index)
		.map((entry) => entry.field);
};

/**
 * A saved sort under the previous storage version, made safe for the current column set: an entry on a
 * retired field is dropped (it would keep sorting by a column nobody can see), and one on a renamed field
 * is pointed at its current field with its stored label removed, so the grid re-labels it from the
 * current sort option instead of showing the old column's name. The value comes straight out of
 * localStorage, so anything not shaped like a sort entry is discarded rather than carried into the grid.
 */
export const sanitizeLegacySortState = (stored: readonly unknown[]): SortingState<Record<string, unknown>>[] =>
	stored.flatMap((entry) => {
		if (!isStoredSortEntry(entry)) {
			console.warn("Discarding a malformed saved multi-facility grid sort entry", entry);
			return [];
		}
		const columns = entry.sortColumns;
		if (columns.some((column) => RETIRED_GRID_FIELDS.has(column))) return [];
		if (!columns.some((column) => Object.hasOwn(LEGACY_GRID_FIELD_RENAMES, column))) return [entry];
		const { label: _staleLabel, ...rest } = entry;
		return [{ ...rest, sortColumns: columns.map(renameLegacyGridField) }];
	});

type StoredSortEntry = SortingState<Record<string, unknown>> & { sortColumns: string[] };

const SORT_ORDERS: ReadonlySet<unknown> = new Set<SortOrder>(["asc", "desc"]);

const isStoredSortEntry = (entry: unknown): entry is StoredSortEntry =>
	typeof entry === "object" &&
	entry !== null &&
	"sortColumns" in entry &&
	isStringArray(entry.sortColumns) &&
	entry.sortColumns.length > 0 &&
	"sortOrder" in entry &&
	SORT_ORDERS.has(entry.sortOrder);
